import { MediaMatcher } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import {
    Injector,
    Type,
} from '@angular/core';
import { Logger } from '@services/logger/abstract.logger';
import { StorageService } from '@services/storage.service';
import { hasSupportForPrefersColorScheme } from '@uipath/angular/utilities';

import { CurrentUserAPI } from 'main/app-state/current-user/current-user.api';
import { ExtendedUserStateManager } from 'main/app-state/generics/user-state-manager';
import {
    BehaviorSubject,
    fromEvent,
    Observable,
    of,
    Subscription,
} from 'rxjs';
import {
    filter,
    pairwise,
} from 'rxjs/operators';

import { ThemeAPI } from './theme.api';
import {
    RenderableTheme,
    Theme,
} from './theme.types';

const THEME_KEY = 'uipath-theme';

/**
 * The current state is the theme that the user selected (auto, light, or dark).
 * ThemeManager handles the theme from the moment it's instantiated, which also includes login/register/etc. forms.
 */
export class ThemeManager extends ExtendedUserStateManager<Theme> implements ThemeAPI {
    protected _state: BehaviorSubject<Theme>;

    private _document: Document;
    private _storage: StorageService;
    private _renderedTheme!: RenderableTheme;
    private _mediaPrefersDark: MediaQueryList;
    private _mediaPrefersDarkSub!: Subscription;

    constructor(
        injector: Injector,
        private _user: CurrentUserAPI,
    ) {
        super(_user);

        this._document = injector.get(DOCUMENT);
        this._storage = injector.get<StorageService>(StorageService);
        const mediaMatcher = injector.get<MediaMatcher>(MediaMatcher);

        this._state = new BehaviorSubject<Theme>(this._storageTheme);

        // The Injector doesn't work directly with abstract classes, so manual casting to Type<Logger> has to be done.
        // More details: https://github.com/angular/angular/issues/23611
        this._logStateChanges('Theme', injector.get<Logger>(Logger as Type<Logger>));

        this._state
            .subscribe(
                (theme) => {
                    const appliedTheme = theme === Theme.auto ? this._determineAutoTheme() : theme;

                    this._storage.set(this._themeKey, theme);
                    this._applyTheme(appliedTheme);
                },
            );

        this._state
            .pipe(
                pairwise(),
                filter(([previousTheme, currentTheme]) => previousTheme !== currentTheme),
            )
            .subscribe(([previousTheme, currentTheme]) => {
                if (previousTheme === Theme.auto) {
                    return this._disposePrefersColorSchemeWatcher();
                }

                if (currentTheme === Theme.auto) {
                    return this._setPrefersColorSchemeWatcher();
                }
            });

        this._mediaPrefersDark = mediaMatcher.matchMedia('(prefers-color-scheme: dark)');

        if (this.value === Theme.auto) {
            this._setPrefersColorSchemeWatcher();
        }
    }

    public get renderedTheme(): RenderableTheme {
        return this._renderedTheme;
    }

    public set(newTheme: Theme) {
        this._state.next(newTheme);
    }

    public fetch() {
        return new Observable<Theme>((subscriber) => {
            this._state.next(this._storageTheme);
            subscriber.next(this._storageTheme);
            subscriber.complete();
        });
    }

    public clean() {
        return of(null);
    }

    private get _themeKey() {
        return this._user.value ?
            `${THEME_KEY}-${this._user.value.tenancyId}-${this._user.value.id}` :
            THEME_KEY;
    }

    private get _storageTheme(): Theme {
        return this._storage.get(
            this._themeKey,
            hasSupportForPrefersColorScheme() ? Theme.auto : Theme.light,
        ) as Theme;
    }

    private _applyTheme(theme: RenderableTheme) {
        const remove = theme === Theme.dark ?
            Theme.light :
            Theme.dark;

        this._renderedTheme = theme;

        this._document.body.classList.remove(remove);
        this._document.body.classList.add(theme);
        this._document.body.dataset['theme'] = theme;
    }

    private _determineAutoTheme(): RenderableTheme {
        if (hasSupportForPrefersColorScheme()) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ?
                Theme.dark : Theme.light;
        }
        return Theme.light;
    }

    private _setPrefersColorSchemeWatcher() {
        if (
            this._mediaPrefersDarkSub instanceof Subscription &&
            !this._mediaPrefersDarkSub.closed
        ) {
            return;
        }

        this._mediaPrefersDarkSub =
            fromEvent<MediaQueryListEvent>(this._mediaPrefersDark, 'change')
                .subscribe(() => {
                    if (this.value === Theme.auto) {
                        this._applyTheme(this._determineAutoTheme());
                    }
                });
    }

    private _disposePrefersColorSchemeWatcher() {
        if (
            this._mediaPrefersDarkSub instanceof Subscription &&
            !this._mediaPrefersDarkSub.closed
        ) {
            this._mediaPrefersDarkSub.unsubscribe();
        }
    }
}
